/**
 * The inference contract: what callers send to `POST /v1/decide` and what every model backend returns.
 *
 * It mirrors the model server (`trodo_s1/schema/questions.py` in the models repo) field for field, so the console
 * validates a request once, forwards it unchanged, and any backend that implements this contract can be registered
 * as a model. `/v1/systemone` takes the same request without `options` (TypeSafe-compatible shape).
 */
import { z } from 'zod'

export const MAX_CHOICE_OPTIONS = 255
export const MIN_SCORE_LEVELS = 2
export const MAX_SCORE_LEVELS = 10
export const ABSTAIN = 'abstain'

export const PRIMITIVES = ['noul', 'score', 'choice', 'extract'] as const
export const EXTRACT_KINDS = ['any', 'number', 'amount', 'date', 'phone', 'email', 'name', 'text'] as const
export type ExtractKind = (typeof EXTRACT_KINDS)[number]
export const MAX_EXTRACT_CANDIDATES = 128
export type Primitive = (typeof PRIMITIVES)[number]

const instructions = z.union([z.string().min(1), z.record(z.string(), z.unknown()), z.array(z.unknown())])
const criteria = z.union([z.array(z.string().min(1)), z.record(z.string().min(1), z.string())])

export function optionNames(q: { type: Primitive; criteria?: string[] | Record<string, string> | null }): string[] {
  if (q.criteria == null || q.type === 'extract') return q.type === 'noul' ? ['yes', 'no'] : []
  return Array.isArray(q.criteria) ? [...q.criteria] : Object.keys(q.criteria)
}

/** `extract` (Hartley extension): the answer is a span of the state. `kind` narrows the candidate spans. */
export const extractSpecSchema = z
  .object({
    kind: z.enum(EXTRACT_KINDS).default('any'),
    max_candidates: z.number().int().min(1).max(MAX_EXTRACT_CANDIDATES).default(48),
  })
  .strict()

export const questionSchema = z
  .object({
    type: z.enum(PRIMITIVES),
    instructions,
    criteria: criteria.nullish(),
    extract: extractSpecSchema.nullish(),
  })
  .strict()
  .superRefine((q, ctx) => {
    const names = optionNames(q)
    if (q.type === 'extract') {
      if (q.criteria != null)
        ctx.addIssue({ code: 'custom', message: 'extract takes no criteria; say what to extract in instructions and narrow it with extract.kind' })
      return
    }
    if (q.extract != null) ctx.addIssue({ code: 'custom', message: 'extract settings only apply to type extract' })
    if (q.type === 'choice') {
      if (names.length < 2) ctx.addIssue({ code: 'custom', message: 'choice needs at least 2 options' })
      if (names.length > MAX_CHOICE_OPTIONS)
        ctx.addIssue({ code: 'custom', message: `choice supports at most ${MAX_CHOICE_OPTIONS} options (got ${names.length})` })
      if (new Set(names).size !== names.length) ctx.addIssue({ code: 'custom', message: 'choice option names must be unique' })
      if (names.includes(ABSTAIN))
        ctx.addIssue({ code: 'custom', message: `'${ABSTAIN}' is reserved; it is added automatically` })
    } else if (q.type === 'score') {
      if (names.length < MIN_SCORE_LEVELS || names.length > MAX_SCORE_LEVELS)
        ctx.addIssue({
          code: 'custom',
          message: `score needs ${MIN_SCORE_LEVELS}-${MAX_SCORE_LEVELS} levels (got ${names.length})`,
        })
    } else if (q.criteria != null && names.length > 2) {
      ctx.addIssue({ code: 'custom', message: 'noul criteria may only describe the true/false cases' })
    }
  })
export type Question = z.infer<typeof questionSchema>

const questions = z
  .record(z.string().min(1).max(128), questionSchema)
  .refine((v) => Object.keys(v).length >= 1, { message: 'at least one question is required' })

export const decideOptionsSchema = z
  .object({
    abstain: z.boolean().default(true),
    tenant: z.string().nullish(),
    calibration: z.enum(['global', 'tenant', 'none']).default('global'),
    two_order_read: z.boolean().default(false),
  })
  .strict()

export const decideRequestSchema = z
  .object({
    model: z.string().min(1).optional(),
    state: z.unknown().refine((v) => v !== undefined && v !== null && v !== '', { message: 'state is required' }),
    questions,
    options: decideOptionsSchema.optional(),
  })
  .strict()
export type DecideRequest = z.infer<typeof decideRequestSchema>

export const systemOneRequestSchema = z
  .object({
    model: z.string().min(1).optional(),
    state: z.unknown().refine((v) => v !== undefined && v !== null && v !== '', { message: 'state is required' }),
    questions,
  })
  .strict()
export type SystemOneRequest = z.infer<typeof systemOneRequestSchema>

export interface NoulAnswer {
  type: 'noul'
  noul: number
  probabilities?: Record<string, number>
  confidence?: number
  abstain?: number
  certainty?: number
}
export interface ChoiceAnswer {
  type: 'choice'
  choice: string
  probabilities: Record<string, number>
  confidence: number
  abstain?: number
  certainty?: number
}
export interface ScoreAnswer {
  type: 'score'
  score: number
  legend?: Record<string, string>
  probabilities: Record<string, number>
  confidence: number
  abstain?: number
  certainty?: number
}
export interface ExtractCandidate {
  value: string
  span: [number, number] // character offsets [start, end) into the serialized state text
  probability: number
}
export interface ExtractAnswer {
  type: 'extract'
  present: boolean // false when the model found no span for it (value and span are then absent)
  value?: string
  span?: [number, number]
  candidates: ExtractCandidate[] // best first
  probabilities?: Record<string, number> // not set by the server; keeps the union uniform for clients
  confidence: number
  abstain?: number
  certainty?: number
}
export type Answer = NoulAnswer | ChoiceAnswer | ScoreAnswer | ExtractAnswer

export interface Usage {
  input_tokens: number
  output_tokens: number
}

export interface DecideResponse {
  model: string
  answers: Record<string, Answer>
  usage: Usage
  latency_ms?: number
  calibration?: string
}

/** Error body for every 4xx/5xx from the public API. */
export interface ApiErrorBody {
  error: { type: string; message: string; details?: string[] }
}

export function formatZodIssues(err: z.ZodError): string[] {
  return err.issues.map((i) => `${i.path.join('.') || '(body)'}: ${i.message}`)
}

/** TypeSafe's confidence: (K * p_max - 1) / (K - 1); 1 when all mass is on one option, 0 when uniform. */
export function confidenceFromProbs(probs: number[]): number {
  const k = probs.length
  if (k < 2) return 1
  return Math.max(0, Math.min(1, (k * Math.max(...probs) - 1) / (k - 1)))
}
